import { useEffect } from 'react';
import { LANGUAGES, getTaxiCopy } from './taxi-i18n';
import { TAXI_THEME } from './taxi-theme';

function setMeta(name, content, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeHreflangAlternates() {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
}

function removeOgLocaleAlternates() {
  document
    .querySelectorAll('meta[property="og:locale:alternate"]')
    .forEach((el) => el.remove());
}

function addOgLocaleAlternate(locale) {
  const el = document.createElement('meta');
  el.setAttribute('property', 'og:locale:alternate');
  el.setAttribute('content', locale);
  document.head.appendChild(el);
}

export default function useTaxiSeo(lang) {
  const copy = getTaxiCopy(lang);
  const { seo } = copy;
  const language = LANGUAGES.find((item) => item.code === copy.lang);

  useEffect(() => {
    const previousTitle = document.title;
    const previousLang = document.documentElement.lang;

    document.documentElement.lang = copy.lang;
    document.title = seo.title;

    setMeta('description', seo.description);
    setMeta('keywords', seo.keywords);
    setMeta('robots', 'noindex, nofollow');
    setLink('canonical', seo.canonical);

    removeHreflangAlternates();
    LANGUAGES.forEach((item) => {
      const href = item.code === 'hr'
        ? 'https://www.seacloud-software.hr/demo/taxi-radenovic'
        : `https://www.seacloud-software.hr/demo/taxi-radenovic/${item.code}`;
      setLink('alternate', href, item.hreflang);
    });
    setLink(
      'alternate',
      'https://www.seacloud-software.hr/demo/taxi-radenovic',
      'x-default',
    );

    setMeta('og:type', 'website', true);
    setMeta('og:url', seo.canonical, true);
    setMeta('og:title', seo.title, true);
    setMeta('og:description', seo.description, true);
    setMeta('og:image', seo.ogImage, true);
    setMeta('og:locale', language?.locale || 'hr_HR', true);

    removeOgLocaleAlternates();
    LANGUAGES.filter((item) => item.code !== copy.lang).forEach((item) => {
      addOgLocaleAlternate(item.locale);
    });

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', seo.title);
    setMeta('twitter:description', seo.description);
    setMeta('twitter:image', seo.ogImage);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'TaxiService',
      name: `Taxi ${TAXI_THEME.brandName}`,
      description: seo.description,
      url: seo.canonical,
      inLanguage: language?.hreflang || 'hr-HR',
      telephone: TAXI_THEME.phone,
      areaServed: ['Pula', 'Poreč', 'Zagreb', 'Venice', 'Ljubljana', 'Trieste', 'Istria'],
      serviceType: copy.schemaServiceTypes,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Poreč',
        addressRegion: 'Istria',
        addressCountry: 'HR',
      },
      priceRange: '$$',
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ],
        opens: '00:00',
        closes: '23:59',
      },
    };

    const scriptId = 'taxi-radenovic-schema';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    window.scrollTo(0, 0);

    return () => {
      document.title = previousTitle;
      document.documentElement.lang = previousLang;
      removeHreflangAlternates();
      removeOgLocaleAlternates();
      const schemaEl = document.getElementById(scriptId);
      if (schemaEl) schemaEl.remove();
    };
  }, [copy.lang, copy.schemaServiceTypes, language?.hreflang, language?.locale, seo.canonical, seo.description, seo.ogImage, seo.keywords, seo.title]);
}
